import { useCallback, useMemo, useState } from "react";
import { Container, Stack, Paper, Typography, Button } from "@mui/material";

import { Link as RouterLink, useLocation } from "react-router-dom";

import { useAuthSession } from "../../../components/AuthSessionProvider";
import { PeopleListCard } from "../components";
import {
  Filters,
  Header,
  ConditionalRenderer,
  TabsFilters,
  CTAContainer,
  useLanguageTranslator,
  DataDisplayMessage,
} from "../../../components";
import { usePeoplesCall } from "../../../hooks";
import { useFilters } from "../helpers";
import { gql, useMutation } from "@apollo/client";
import { useNotify } from "../../../components/@common/NotificationsProvider";

const personDeleteMutationDocument = gql`
  mutation personDelete($person: PersonDeleteInput!) {
    personDelete(input: $person) {
      person {
        id
      }
    }
  }
`;

function PeopleList() {
  const [filters, setFilters] = useState({
    country: "",
    startYear: "",
    endYear: "",
    theme: "",
  });
  const [inputValue, setInputValue] = useState("");
  const [currTab, setCurrTab] = useState("all");
  const [limit, setLimit] = useState(8);

  const location = useLocation();
  const { translator } = useLanguageTranslator();
  const notify = useNotify();
  const { user } = useAuthSession() as any;
  const isPrivate = location?.pathname?.includes("/submission/people") || false;
  const isLoggedIn = !!user;

  const { data, loading, refetch } = usePeoplesCall({
    isPrivate,
    user,
    variables: {
      limit: limit,
      ...(filters?.country && filters?.country !== "0"
        ? { countryId: Number(filters?.country) }
        : {}),
      ...(filters?.theme && filters?.theme !== "0"
        ? { themeId: Number(filters?.theme) }
        : {}),
      ...(filters?.startYear ? { startYear: Number(filters?.startYear) } : {}),
      ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
    },
  });

  const [deletePeopleMutation, { loading: deletingPeople }] = useMutation(
    personDeleteMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const { tabsData, people = [] }: any = useFilters({ isPrivate, data });

  const filteredDataResults = useMemo(() => {
    if (currTab === "all") return people;
    return people?.filter(
      (item: any) => item.status?.toLowerCase() === currTab
    );
  }, [people, currTab]);

  const modifiedData = isPrivate
    ? filteredDataResults
    : data?.people?.collection;

  function onClickHandler() {
    const scrollPosition = window.scrollY;
    setLimit(limit + 8);

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }

  const hasMore =
    limit === data?.people?.metadata?.totalCount ||
    limit > data?.people?.metadata?.totalCount ||
    !data?.people?.metadata?.totalCount;

  const filteredPeopleList = useMemo(() => {
    if (!inputValue) return modifiedData || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return modifiedData?.filter((item: any) => {
      const firstName = item?.firstname || "";
      const lastName = item?.lastname || "";

      const normalizedFirstName = firstName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();
      const normalizedLastName = lastName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();

      return (
        normalizedFirstName.includes(searchValue) ||
        normalizedLastName.includes(searchValue)
      );
    });
  }, [inputValue, modifiedData]);

  const handleDelete = useCallback(
    async (clickedItem: any) => {
      if (await notify.confirm(`Deleting Person. This cannot be undone.`)) {
        try {
          await deletePeopleMutation({
            variables: { person: { id: clickedItem?.id } },
          });
          notify.success(`People Deleted successfully!`);
          refetch();
        } catch (err) {
          notify.error(err as Error);
        }
      }
    },
    [deletePeopleMutation, notify]
  );

  return (
    <div>
      <Header
        title={translator("People")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />

      <Container sx={{ my: 3 }}>
        <ConditionalRenderer condition={isPrivate}>
          <TabsFilters
            currTab={currTab}
            setCurrTab={setCurrTab}
            listItems={tabsData}
            heading={`${translator("List of")} ${translator("People")}`}
            isAllShow={isPrivate ? true : false}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={!isPrivate}>
          <Filters setFilters={setFilters} filters={filters} />
        </ConditionalRenderer>
        <Stack gap={2}>
          {filteredPeopleList?.map((elem: any, i: any) => (
            <PeopleListCard
              key={`people-list-card-${elem.id}`}
              elem={elem}
              isPrivate={isPrivate}
              handleDelete={() => handleDelete(elem)}
              editPath={`/people/${elem?.id}/edit`}
            />
          ))}
          <DataDisplayMessage data={filteredPeopleList} loading={loading} />
        </Stack>
      </Container>

      <ConditionalRenderer condition={!hasMore}>
        <Container sx={{ my: 3 }}>
          <Paper sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <Button onClick={onClickHandler}>{translator("Show more")}</Button>
          </Paper>
        </Container>
      </ConditionalRenderer>

      <ConditionalRenderer condition={isLoggedIn && isPrivate}>
        <Container sx={{ my: 3 }}>
          <CTAContainer sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" mb={2}>
              {translator("Add new people")}
            </Typography>
            <Button
              component={RouterLink}
              to="/submission/people/new"
              sx={{
                px: 6,
                backgroundColor: "#ffffff",
                color: "primary.dark",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {translator("generic.CREATE")}
            </Button>
          </CTAContainer>
        </Container>
      </ConditionalRenderer>
    </div>
  );
}

export { PeopleList };
